/**
 * Nuevo pedido — origin picker counter / mesa / domicilio. Mesa usa el
 * floor plan picker visual (mismo que desktop). Delivery form tiene los
 * mismos campos que desktop.
 */

import { Button, Card, CardContent } from '@atreus/ui';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowLeft, Bike, Store } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';

import { OriginTile } from '../components/OriginTile.js';
import { GUEST_NAME_MAX } from '../features/orders/OrderGuestDialog.js';
import { parseConfig } from '../features/tables/floorPlan.js';
import { TableOriginTile } from '../features/tables/TableOriginTile.js';
import {
  TablesFloorPlanPicker,
  type SelectedTableInfo,
} from '../features/tables/TablesFloorPlanPicker.js';
import { useFeatures } from '../lib/features.js';
import { trpc } from '../lib/trpc.js';

import type { OrderOrigin } from '@atreus/core';

interface DeliveryForm {
  line1: string;
  city: string;
  phone: string;
  reference: string;
}

const emptyDelivery: DeliveryForm = {
  line1: '',
  city: '',
  phone: '',
  reference: '',
};

export const OrderNewPage = () => {
  const navigate = useNavigate();
  const features = useFeatures();
  const tenantData = trpc.tenant.current.useQuery();
  const defaultCity = tenantData.data?.tenant.municipality ?? '';

  // Deep-link desde el dashboard de mesas (`/orders/new?table=<label>`).
  const urlSearch = useSearch({ strict: false }) as { table?: string };
  const preselectLabel = urlSearch.table ?? null;

  // Pedidos activos para superponer badge "N pedidos" en mesas ocupadas.
  // Filtro server-side con `activeOnly` para no traer histórico.
  const ordersQuery = trpc.orders.list.useQuery(
    { activeOnly: true, limit: 500 },
    {
      enabled: features.isEnabled('tables'),
      refetchInterval: 15_000,
    },
  );
  const busyCountByTableId = useMemo(() => {
    const m = new Map<string, number>();
    for (const o of ordersQuery.data?.items ?? []) {
      if (o.origin !== 'table') continue;
      if (!o.tableId) continue;
      m.set(o.tableId, (m.get(o.tableId) ?? 0) + 1);
    }
    return m;
  }, [ordersQuery.data]);

  const [origin, setOrigin] = useState<OrderOrigin>(
    preselectLabel && features.isEnabled('tables') ? 'table' : 'counter',
  );
  const [selectedTable, setSelectedTable] = useState<SelectedTableInfo | null>(
    null,
  );
  const [delivery, setDelivery] = useState<DeliveryForm>(emptyDelivery);
  const [guestName, setGuestName] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Cuando viene ?table=<label>, resolvemos a SelectedTableInfo via config.
  useEffect(() => {
    if (!preselectLabel || selectedTable) return;
    const rawCfg = tenantData.data?.features.find(
      (f) => f.featureId === 'tables',
    )?.configJson;
    const cfg = parseConfig(rawCfg);
    if (!cfg) return;
    for (const zone of cfg.zones) {
      const match = zone.tables.find(
        (t) => t.label === preselectLabel && !t.blocked,
      );
      if (match) {
        setSelectedTable({
          id: match.id,
          label: match.label,
          capacity: match.capacity,
          zoneName: zone.name,
        });
        return;
      }
    }
  }, [preselectLabel, selectedTable, tenantData.data]);

  // Pre-llena city con el municipio configurado en el tenant, solo si el
  // usuario no lo editó ya.
  useEffect(() => {
    if (defaultCity && delivery.city === '') {
      setDelivery((d) => ({ ...d, city: defaultCity }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCity]);

  const line1Id = useId();
  const cityId = useId();
  const phoneId = useId();
  const refId = useId();
  const guestId = useId();

  const create = trpc.orders.create.useMutation({
    onSuccess: ({ id }) => {
      void navigate({ to: '/orders/$orderId', params: { orderId: id } });
    },
    onError: (e) => setError(e.message),
  });

  const submit = (): void => {
    setError(null);
    const guest = guestName.trim() ? { guestName: guestName.trim() } : {};
    if (origin === 'table') {
      if (!selectedTable) {
        setError('Selecciona una mesa en el plano.');
        return;
      }
      const numeric = /^\d+$/.test(selectedTable.label)
        ? Number(selectedTable.label)
        : undefined;
      create.mutate({
        origin: 'table',
        tableId: selectedTable.id,
        tableLabel: selectedTable.label,
        ...(numeric !== undefined ? { tableNumber: numeric } : {}),
        ...guest,
      });
      return;
    }
    if (origin === 'delivery') {
      if (delivery.line1.trim().length === 0) {
        setError('La dirección es obligatoria para domicilio.');
        return;
      }
      if (delivery.city.trim().length === 0) {
        setError('La ciudad es obligatoria.');
        return;
      }
      create.mutate({
        origin: 'delivery',
        deliveryAddress: {
          line1: delivery.line1.trim(),
          city: delivery.city.trim(),
          phone: delivery.phone.trim() || null,
          reference: delivery.reference.trim() || null,
          line2: null,
          neighborhood: null,
          municipalityCode: null,
        },
        ...guest,
      });
      return;
    }
    create.mutate({ origin: 'counter', ...guest });
  };

  const allowedCount =
    1 +
    (features.isEnabled('tables') ? 1 : 0) +
    (features.isEnabled('delivery') ? 1 : 0);

  return (
    <div className="mx-auto max-w-3xl p-8">
      <div className="mb-6">
        <Button asChild variant="ghost" size="sm">
          <Link to="/orders">
            <ArrowLeft className="size-4" /> Volver
          </Link>
        </Button>
      </div>

      <h1 className="text-3xl font-black tracking-tight">Nuevo pedido</h1>
      <p className="mt-1 text-fg-muted">
        Elige el origen para empezar a agregar productos.
      </p>

      <div
        className={
          'mt-6 grid grid-cols-1 gap-3 ' +
          (allowedCount >= 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')
        }
      >
        <OriginTile
          icon={Store}
          label="Mostrador"
          description="Cliente paga en el acto."
          selected={origin === 'counter'}
          onClick={() => setOrigin('counter')}
        />
        {features.isEnabled('tables') ? (
          <TableOriginTile
            selected={origin === 'table'}
            onClick={() => setOrigin('table')}
          />
        ) : null}
        {features.isEnabled('delivery') ? (
          <OriginTile
            icon={Bike}
            label="Domicilio"
            description="A la dirección del cliente."
            selected={origin === 'delivery'}
            onClick={() => setOrigin('delivery')}
          />
        ) : null}
      </div>

      {origin === 'table' && features.isEnabled('tables') ? (
        <Card className="mt-6">
          <CardContent className="space-y-3 p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold">
                {selectedTable
                  ? `Mesa seleccionada: ${selectedTable.label} · ${selectedTable.capacity} pax · ${selectedTable.zoneName}`
                  : 'Selecciona una mesa en el plano'}
              </span>
              {selectedTable ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedTable(null)}
                >
                  Limpiar
                </Button>
              ) : null}
            </div>
            <TablesFloorPlanPicker
              value={selectedTable?.label ?? null}
              valueById={selectedTable?.id ?? null}
              onSelect={setSelectedTable}
              busyCountByTableId={busyCountByTableId}
            />
          </CardContent>
        </Card>
      ) : null}

      {origin === 'delivery' && features.isEnabled('delivery') ? (
        <Card className="mt-6">
          <CardContent className="space-y-4 p-6">
            <div>
              <label
                htmlFor={line1Id}
                className="block text-sm font-semibold tracking-tight"
              >
                Dirección *
              </label>
              <input
                id={line1Id}
                autoFocus
                value={delivery.line1}
                onChange={(e) =>
                  setDelivery({ ...delivery, line1: e.target.value })
                }
                placeholder="Cra 7 #45-32 apto 302"
                maxLength={200}
                className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-base outline-none focus:border-fg"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <label
                  htmlFor={cityId}
                  className="block text-sm font-semibold tracking-tight"
                >
                  Ciudad *
                </label>
                <input
                  id={cityId}
                  value={delivery.city}
                  onChange={(e) =>
                    setDelivery({ ...delivery, city: e.target.value })
                  }
                  placeholder="Bogotá"
                  className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-base outline-none focus:border-fg"
                />
              </div>
              <div>
                <label
                  htmlFor={phoneId}
                  className="block text-sm font-semibold tracking-tight"
                >
                  Teléfono
                </label>
                <input
                  id={phoneId}
                  inputMode="tel"
                  value={delivery.phone}
                  onChange={(e) =>
                    setDelivery({ ...delivery, phone: e.target.value })
                  }
                  placeholder="300 000 0000"
                  className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-base outline-none focus:border-fg"
                />
              </div>
            </div>
            <div>
              <label
                htmlFor={refId}
                className="block text-sm font-semibold tracking-tight"
              >
                Referencia / indicaciones
              </label>
              <textarea
                id={refId}
                rows={2}
                value={delivery.reference}
                onChange={(e) =>
                  setDelivery({ ...delivery, reference: e.target.value })
                }
                placeholder="Casa verde al lado del parque"
                className="mt-1 w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-base outline-none focus:border-fg"
              />
            </div>
          </CardContent>
        </Card>
      ) : null}

      <div className="mt-6">
        <label
          htmlFor={guestId}
          className="block text-sm font-semibold tracking-tight"
        >
          Nombre del cliente{' '}
          <span className="font-normal text-fg-muted">(opcional)</span>
        </label>
        <input
          id={guestId}
          value={guestName}
          onChange={(e) => setGuestName(e.target.value)}
          maxLength={GUEST_NAME_MAX}
          placeholder={
            origin === 'table'
              ? 'Para identificar la mesa, ej. Juan'
              : 'Ej. Juan'
          }
          className="mt-1 w-full rounded-md border border-border bg-surface px-3 py-2 text-base outline-none focus:border-fg sm:max-w-sm"
        />
      </div>

      {error ? (
        <div className="mt-6 rounded-md border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">
          {error}
        </div>
      ) : null}

      <div className="mt-6 flex gap-3">
        <Button size="lg" onClick={submit} disabled={create.isPending}>
          {create.isPending ? 'Creando…' : 'Crear pedido'}
        </Button>
        <Button asChild variant="ghost" size="lg">
          <Link to="/orders">Cancelar</Link>
        </Button>
      </div>
    </div>
  );
};
